import React from "react";
import SectionTitle from "../../components/layouts/others/title-section.layout";
import { Box, Container, Divider, Paper, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBullhorn,
    faCheckCircle,
    faGavel,
    faLandmark,
    faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    LabelList,
    Cell,
    ReferenceLine,
} from "recharts";

// @ts-ignore
import imgJpg from "../../assets/images/others/jpg-5.png";

const indexEvolutionData = [
    { year: "2018", chargesIndex: 100, inflationIndex: 100 },
    { year: "2024", chargesIndex: 161, inflationIndex: 116 },
];

const hausseMData = [
    { poste: "Charges de personnel", hausseM: 1.1, color: "#1565c0" },
    { poste: "Achats et charges externes", hausseM: 0.9, color: "#ef6c00" },
    { poste: "Autres charges", hausseM: 1.1, color: "#8d6e63" },
];

const effectifsData = [
    { year: "2021", agents: 81 },
    { year: "2024", agents: 93 },
];

const achatsDetailsData = [
    { poste: "Achats et prestations (+90%)", deltaK: 293, color: "#ef6c00" },
    { poste: "Énergie (+77%)", deltaK: 198, color: "#ffb300" },
    { poste: "Fêtes/Réceptions (+20%)", deltaK: 13, color: "#4caf50" },
    { poste: "Frais d’actes/contentieux (+186%)", deltaK: 17, color: "#9c27b0" },
];

const rappelsData = [
    { label: "Charges de fonctionnement", variation: 61, color: "#c62828" },
    { label: "Trésorerie", variation: -51, color: "#ef6c00" },
    { label: "Endettement", variation: 399, color: "#ad1457" },
    { label: "Impôts fonciers", variation: 26, color: "#1565c0" },
];

const POST20260222: React.FC = () => {
    const Bullet = ({ children }: { children: React.ReactNode }) => (
        <Box sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}>
            <Typography sx={{ color: "#ef6c00", fontWeight: 900, mt: "2px" }}>•</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                {children}
            </Typography>
        </Box>
    );

    const StatCard = ({ value, label }: { value: string; label: string }) => (
        <Paper
            elevation={0}
            sx={{
                p: 2.2,
                borderRadius: 3,
                textAlign: "center",
                border: "1px solid rgba(198,40,40,0.25)",
                bgcolor: "#fff5f5",
            }}
        >
            <Typography variant="h4" fontWeight={900} color="#c62828">
                {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </Paper>
    );

    return (
        <Container maxWidth="lg" sx={{ mb: 8 }}>
            <SectionTitle title="ESPACE INFORMATIONS DES HABITANTS" />

            <Box mt={3} mb={5}>
                <Typography variant="h6" fontWeight={700} align="center" gutterBottom color="primary">
                    <FontAwesomeIcon icon={faBullhorn} style={{ marginRight: 8 }} />
                    VLD2026 - Complément d'information n°3 (22&nbsp;février&nbsp;2026)
                </Typography>
                <Divider sx={{ mt: 1, mb: 3 }} />
            </Box>

            <Stack spacing={5}>
                <Paper
                    elevation={4}
                    sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: 4,
                        border: "1px solid rgba(25,118,210,0.14)",
                        background:
                            "linear-gradient(135deg, rgba(13,71,161,0.06) 0%, rgba(25,118,210,0.03) 55%, rgba(67,160,71,0.05) 100%)",
                    }}
                >
                    <Typography variant="h4" fontWeight={900} color="#0d47a1" sx={{ mb: 2 }}>
                        Complément d’information n°3 sur le tract n°3 dédié aux finances
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Nous souhaitons apporter une information complémentaire pour enrichir le débat public.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Vous avez été nombreux à nous questionner sur l’évolution des charges de fonctionnement à Bois-le-Roi. Voici un
                        complément d’information dédié.
                    </Typography>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, borderLeft: "6px solid #43a047" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#2e7d32" }} />
                        <Typography variant="h5" fontWeight={900} color="#2e7d32">
                            Synthèse
                        </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                            Contrairement à ce qu’affirme la majorité actuelle, la dérive des dépenses de fonctionnement ne résulte pas des
                            crises successives (sanitaire, énergétique, etc.) ni d’aléas conjoncturels, mais bien d’une gestion financière
                            défaillante.
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                            Contrairement à ce qu’affirme la majorité actuelle, la dérive des dépenses de fonctionnement ne résulte pas des
                            crises successives (sanitaire, énergétique, etc.) ni d’aléas conjoncturels, mais bien d’une gestion financière
                            défaillante.
                        </Typography>
                    </Stack>

                    <Grid container spacing={2} sx={{ mt: 1, justifyContent: "center" }}>
                        <Grid size={{ xs: 12, md: 8, lg: 7 }}>
                            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, border: "1px solid #e0e0e0", maxWidth: 560, mx: "auto" }}>
                                <Box
                                    component="img"
                                    src={imgJpg}
                                    alt="Jean-Philippe Guibert"
                                    sx={{
                                        width: { xs: 260, md: 320 },
                                        height: { xs: 260, md: 320 },
                                        objectFit: "cover",
                                        objectPosition: "50% 18%",
                                        borderRadius: "50%",
                                        border: "6px solid #1565c0",
                                        boxShadow: "0 12px 28px rgba(21,101,192,0.24)",
                                        display: "block",
                                        mx: "auto",
                                    }}
                                />
                                <Typography variant="caption" display="block" align="center" sx={{ mt: 1 }}>
                                    Photo JP
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Typography variant="body1" color="text.secondary" sx={{ mt: 3, lineHeight: 1.75 }}>
                        Entre 2018 et 2024, notre commune a enregistré la plus forte augmentation des charges de fonctionnement parmi les
                        communes voisines : +61%.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
                        La responsabilité de la majorité actuelle dans la dérive des charges de fonctionnement est accablante : recours accru
                        et systématique à des prestataires externes, augmentation des effectifs d’agents communaux sans création ni
                        amélioration significative des services publics, ainsi qu’une absence de maîtrise et d’efforts budgétaires.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
                        Un changement de cap est possible à partir de mars 2026, afin de rétablir une gestion plus rigoureuse et durable des
                        finances communales.
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <StatCard value="+61%" label="Charges de fonctionnement" />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <StatCard value="+3,1M€" label="Dépenses supplémentaires" />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <StatCard value="+40%" label="Charges de personnel" />
                        </Grid>
                    </Grid>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faGavel} style={{ color: "#1565c0" }} />
                        <Typography variant="h5" fontWeight={900} color="#1565c0">
                            Évolution des charges de fonctionnement à Bois-le-Roi
                        </Typography>
                    </Stack>

                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Élément de définition :
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Les charges de fonctionnement sont l’ensemble des dépenses nécessaires au fonctionnement des services de la commune.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
                        Entre 2018 et 2024, les charges de fonctionnement ont augmenté de +61%, soit près de 3,1M€ supplémentaires.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
                        Pour rappel (voir notre complément d’information n°1), Bois-le-Roi a connu l’augmentation des charges de
                        fonctionnement la plus importante des communes voisines, et près de 4 fois supérieure à l’inflation sur la période.
                    </Typography>

                    <Box sx={{ height: 330, mt: 2, bgcolor: "#fafafa", borderRadius: 3, p: 2, border: "1px solid #eee" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={indexEvolutionData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                <XAxis dataKey="year" />
                                <YAxis domain={[0, 170]} />
                                <Tooltip formatter={(value: any) => `${value}`} />
                                <Bar dataKey="chargesIndex" name="Charges (indice 100 en 2018)" fill="#c62828" radius={[8, 8, 0, 0]}>
                                    <LabelList dataKey="chargesIndex" position="top" />
                                </Bar>
                                <Bar dataKey="inflationIndex" name="Inflation (indice 100 en 2018)" fill="#2e7d32" radius={[8, 8, 0, 0]}>
                                    <LabelList dataKey="inflationIndex" position="top" />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                        Lecture: base 100 en 2018. En 2024, les charges atteignent 161 contre 116 pour l’inflation.
                    </Typography>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, borderLeft: "6px solid #ef6c00" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faLandmark} style={{ color: "#ef6c00" }} />
                        <Typography variant="h5" fontWeight={900} color="#ef6c00">
                            Éléments de définition
                        </Typography>
                    </Stack>

                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Les charges de personnel sont constituées de la masse salariale des agents communaux.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Les achats et charges externes sont constitués des frais de fournitures, d’énergies et prestations nécessaires au
                        fonctionnement des services de la commune.
                    </Typography>

                    <Stack spacing={1} sx={{ mt: 2 }}>
                        <Bullet>Les charges de personnel représentent le premier poste communal.</Bullet>
                    </Stack>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Entre 2018 et 2024, les charges de personnel ont augmenté de 40%, soit près de 1,1M€.
                    </Typography>

                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                        <Bullet>Les achats et charges externes représentent le second poste communal.</Bullet>
                    </Stack>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Sur la période, les achats et charges externes ont augmenté de 58%, soit près de 0,9M€.
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <StatCard value="+40%" label="Charges de personnel (+1,1M€)" />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <StatCard value="+58%" label="Achats et charges externes (+0,9M€)" />
                        </Grid>
                    </Grid>

                    <Box sx={{ height: 320, mt: 2, bgcolor: "#fafafa", borderRadius: 3, p: 2, border: "1px solid #eee" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={hausseMData} layout="vertical" margin={{ top: 10, right: 20, left: 30, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                <XAxis type="number" domain={[0, 1.2]} unit=" M€" />
                                <YAxis type="category" dataKey="poste" width={170} />
                                <Tooltip formatter={(value: any) => `+${String(value).replace(".", ",")} M€`} />
                                <Bar dataKey="hausseM" radius={[0, 8, 8, 0]}>
                                    <LabelList dataKey="hausseM" position="right" formatter={(value: any) => `+${String(value).replace(".", ",")} M€`} />
                                    {hausseMData.map((entry, index) => (
                                        <Cell key={`postes-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                        Décomposition de la hausse totale des charges de fonctionnement: +3,1M€ entre 2018 et 2024.
                    </Typography>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>
                    <Typography variant="h5" fontWeight={900} color="#1565c0" sx={{ mb: 2 }}>
                        Explications de l’évolution des charges de fonctionnement
                    </Typography>

                    <Typography variant="h6" fontWeight={800} color="#37474f" sx={{ mb: 1 }}>
                        Comment expliquer la hausse des charges de personnel :
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Entre 2018 et 2024, les charges de personnel ont augmenté de 40%. Cette hausse s’explique principalement par
                        l’évolution du nombre d’agents et des évolutions de rémunération.
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        <Bullet>La croissance des effectifs communaux, sous la responsabilité directe de la commune</Bullet>
                    </Stack>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Le nombre d’agents communaux a augmenté de 15% entre 2021 et 2024, soit près de 12 agents supplémentaires.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        En 2024, parmi les 93 agents communaux, 31 sont affectés au service technique, 22 au service administratif, 20 à la
                        filière animation, 9 à la filière sociale, 4 à la police municipale et 7 sont affectés à d’autres services.
                    </Typography>

                    <Box sx={{ height: 260, mt: 2, bgcolor: "#fafafa", borderRadius: 3, p: 2, border: "1px solid #eee" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={effectifsData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                <XAxis dataKey="year" />
                                <YAxis domain={[70, 100]} />
                                <Tooltip formatter={(value: any) => `${value} agents`} />
                                <Bar dataKey="agents" fill="#1565c0" radius={[8, 8, 0, 0]}>
                                    <LabelList dataKey="agents" position="top" />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                        Effectifs communaux: +12 agents entre 2021 et 2024 (+15%).
                    </Typography>

                    <Stack spacing={1} sx={{ mt: 1 }}>
                        <Bullet>Les évolutions, sous la responsabilité directe de la commune</Bullet>
                        <Bullet>L’évolution du régime indemnitaire et la rémunération,</Bullet>
                        <Bullet>
                            Les moyens temporaires supplémentaires alloués aux services (ex : heures supplémentaires, emplois saisonniers),
                        </Bullet>
                        <Bullet>Les remplacements,</Bullet>
                        <Bullet>L’évolution du périmètre de gestion (création, suppression, ou extension de services).</Bullet>
                        <Bullet>Les autres évolutions sous la responsabilité de l’État</Bullet>
                        <Bullet>
                            La hausse conjoncturelle : augmentation réglementaire fixée par décret ministériel, applicable à l’ensemble des
                            fonctionnaires, concrétisée par une augmentation du traitement en pourcentage ou en nombre de points indiciaires,
                        </Bullet>
                        <Bullet>La revalorisation des échelles indiciaires,</Bullet>
                        <Bullet>L’évolution des taux de charges patronales,</Bullet>
                        <Bullet>
                            Le glissement vieillesse technicité (G.V.T) : durées minimales et normales d’avancement fixées par décret.
                        </Bullet>
                    </Stack>

                    <Typography variant="h6" fontWeight={800} color="#37474f" sx={{ mt: 3, mb: 1 }}>
                        Comment expliquer la hausse des achats et charges externes :
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Entre 2018 et 2024, les achats et charges externes ont augmenté de 58%. Cette hausse s’explique principalement par
                        l’évolution des deux postes suivants.
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        <Bullet>
                            La hausse des achats et prestations de services, sous la responsabilité directe de la commune
                        </Bullet>
                    </Stack>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Depuis 2018, la majorité a fait le choix d’accroitre l’externalisation de services. Entre 2018 et 2024, le budget
                        des « achats et prestations de service » a augmenté de 90%, soit près de 293k€ supplémentaires.
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        <Bullet>La hausse conjoncturelle des coûts de l’énergie</Bullet>
                    </Stack>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Depuis 2018, en raison de la crise énergétique, le budget énergie de la commune a très fortement cru. Le budget
                        « énergie » a augmenté de 77%, soit près de 198k€ supplémentaires.
                    </Typography>

                    <Box sx={{ height: 330, mt: 2, bgcolor: "#fafafa", borderRadius: 3, p: 2, border: "1px solid #eee" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={achatsDetailsData} layout="vertical" margin={{ top: 10, right: 20, left: 40, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                <XAxis type="number" domain={[0, 320]} unit=" k€" />
                                <YAxis type="category" dataKey="poste" width={190} />
                                <Tooltip formatter={(value: any) => `+${value} k€`} />
                                <Bar dataKey="deltaK" radius={[0, 8, 8, 0]}>
                                    <LabelList dataKey="deltaK" position="right" formatter={(value: any) => `+${value} k€`} />
                                    {achatsDetailsData.map((entry, index) => (
                                        <Cell key={`externes-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                        Visualisation en montants supplémentaires (k€) pour mieux comparer l’impact budgétaire des postes.
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.75 }}>
                        Par ailleurs, bien que financièrement plus marginales, et alors que la majorité actuelle augmentait les impôts locaux
                        de 26%, d’autres charges, non essentielles et sous la responsabilité de la commune, ont connu une forte inflation.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Les budgets relatifs aux « Fêtes, Cérémonies et Réceptions » et aux « Frais d’actes et contentieux » ont augmenté,
                        respectivement, de 20%, soit près de 13k€ supplémentaires et de 186%, soit près de 17k€ supplémentaires.
                    </Typography>
                </Paper>

                <Paper
                    elevation={2}
                    sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: 4,
                        borderLeft: "6px solid #c62828",
                        bgcolor: "#fffaf9",
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#c62828" }} />
                        <Typography variant="h5" fontWeight={900} color="#c62828">
                            Rappel Compléments d’information n°1 & n°2
                        </Typography>
                    </Stack>

                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Pour rappel (voir notre complément n°1), en 2018, Bois-le-Roi bénéficiait d’une situation financière solide,
                        largement supérieure à celle de sa strate. En 2024, Bois-le-Roi n’est désormais plus qu’une commune dont la gestion
                        financière est médiocre.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.75 }}>
                        Entre 2018 et 2024 et en comparaison des autres communes alentours, Bois-le-Roi a connu :
                    </Typography>

                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "white", border: "1px solid #eee" }}>
                            <Typography variant="subtitle1" fontWeight={900} color="#c62828" sx={{ mb: 1 }}>
                                • La plus forte augmentation des charges de fonctionnement
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                                Bois-le-Roi affiche la pire performance : ses charges de fonctionnement ont augmenté de 61%, soit une
                                progression près de 4 fois supérieure à l’inflation.
                            </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "white", border: "1px solid #eee" }}>
                            <Typography variant="subtitle1" fontWeight={900} color="#c62828" sx={{ mb: 1 }}>
                                • La deuxième plus forte baisse de sa trésorerie, après Fontainebleau
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                                Alors que sept communes ont accru leur trésorerie, Bois-le-Roi fait au contraire, partie des trois communes
                                ayant enregistré une baisse de leur trésorerie : -51%
                            </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "white", border: "1px solid #eee" }}>
                            <Typography variant="subtitle1" fontWeight={900} color="#c62828" sx={{ mb: 1 }}>
                                • La plus forte hausse de l’endettement
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                                En matière d’endettement, Bois-le-Roi détient un triste record : +399%, soit une progression 10 fois
                                supérieure à celle de la commune affichant la deuxième plus forte hausse.
                            </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "white", border: "1px solid #eee" }}>
                            <Typography variant="subtitle1" fontWeight={900} color="#c62828" sx={{ mb: 1 }}>
                                • L’augmentation la plus importante des impôts locaux
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                                Bois-le-Roi a connu la plus forte hausse du taux communal des impôts fonciers (sur les propriétés bâties)
                                parmi les communes voisines : +26%.
                            </Typography>
                        </Paper>
                    </Stack>

                    <Box sx={{ height: 340, mt: 2, bgcolor: "#fafafa", borderRadius: 3, p: 2, border: "1px solid #eee" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={rappelsData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                <XAxis dataKey="label" />
                                <YAxis domain={[-80, 420]} unit="%" />
                                <ReferenceLine y={0} stroke="#616161" />
                                <Tooltip formatter={(value: any) => `${value}%`} />
                                <Bar dataKey="variation" radius={[8, 8, 0, 0]}>
                                    <LabelList dataKey="variation" position="top" formatter={(value: any) => `${value}%`} />
                                    {rappelsData.map((entry, index) => (
                                        <Cell key={`rappel-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                        Variations 2018-2024: la trésorerie est affichée en négatif, les autres indicateurs en hausse.
                    </Typography>
                </Paper>
            </Stack>
        </Container>
    );
};

export default POST20260222;
